import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LEGAL_ADDRESS,
  LEGAL_BUSINESS_NAME,
  LEGAL_CONTACT_EMAIL,
  LEGAL_DOMAIN,
  LEGAL_SITE_NAME,
  LEGAL_SITE_URL,
  LEGAL_TAX_ID,
  LEGAL_URLS,
} from '../../config/legal';
import { LegalPageShell } from './LegalPageShell';

export function TermsOfServicePage() {
  useEffect(() => {
    document.title = `Condiciones del servicio | ${LEGAL_SITE_NAME}`;
  }, []);

  return (
    <LegalPageShell title="Condiciones del servicio" updatedAt="2 de septiembre de 2026">
      <p>
        Las presentes Condiciones del servicio regulan el acceso y uso de{' '}
        <strong>{LEGAL_SITE_NAME}</strong> ({LEGAL_SITE_URL}), incluido el software TPV de
        escritorio, el panel web, los servicios en la nube, el soporte y las integraciones
        disponibles en {LEGAL_DOMAIN}.
      </p>
      <p>
        Al registrarte, descargar el software o utilizar nuestros servicios, declaras haber leído y
        aceptado estas condiciones y nuestra{' '}
        <Link to={LEGAL_URLS.privacy}>Política de privacidad</Link>.
      </p>

      <h2>1. Identificación del prestador</h2>
      <p>
        El servicio {LEGAL_SITE_NAME} ({LEGAL_SITE_URL}) es prestado por{' '}
        <strong>{LEGAL_BUSINESS_NAME}</strong>, con NIF {LEGAL_TAX_ID} y domicilio en{' '}
        {LEGAL_ADDRESS}. Para consultas contractuales o de soporte:{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>.
      </p>

      <h2>2. Descripción del servicio</h2>
      <p>{LEGAL_SITE_NAME} es un software de punto de venta para hostelería que incluye, con todos sus módulos en la suscripción:</p>
      <ul>
        <li>Aplicación TPV para Windows.</li>
        <li>Panel web con informes, licencias y gestión de cuenta.</li>
        <li>Sincronización de datos operativos con la nube.</li>
        <li>Atención gratuita sobre cuenta y facturación, y corrección de errores del propio Hosty. Las intervenciones técnicas y la instalación se contratan aparte.</li>
        <li>VeriFactu incluido, sujeto a completar su configuración fiscal y los trámites necesarios.</li>
      </ul>
      <p>
        Podemos actualizar funcionalidades, interfaces o requisitos técnicos del servicio para
        mejorarlo o adaptarlo a la normativa.
      </p>

      <h2>3. Registro y cuenta</h2>
      <ul>
        <li>Debes proporcionar información veraz y mantenerla actualizada.</li>
        <li>Eres responsable de la confidencialidad de tus credenciales de acceso.</li>
        <li>Debes notificarnos cualquier uso no autorizado de tu cuenta.</li>
        <li>El servicio está destinado a uso profesional en negocios de hostelería.</li>
      </ul>

      <h2>4. Licencias y suscripción</h2>
      <ul>
        <li>
          El acceso al software y servicios cloud puede requerir una suscripción de pago según los
          precios publicados en {LEGAL_SITE_URL}/precios.
        </li>
        <li>Los pagos se procesan a través de Stripe conforme a sus propias condiciones.</li>
        <li>
          La modalidad autogestionada no tiene permanencia y puede cancelarse desde el panel o por
          solicitud a soporte.
        </li>
        <li>
          La modalidad gestionada incluye la puesta en marcha y tiene un compromiso inicial de 12
          meses, conforme al presupuesto aceptado.
        </li>
        <li>
          La licencia concedida es personal, no exclusiva e intransferible, limitada al uso del
          software conforme a estas condiciones.
        </li>
      </ul>

      <h3>4.1. Cobros de la carta digital</h3>
      <ul>
        <li>
          Para cobrar desde la carta digital, el establecimiento debe completar el alta y la
          verificación de una cuenta conectada de Stripe y facilitar una cuenta bancaria válida.
        </li>
        <li>
          Los importes cobrados pertenecen al establecimiento. Stripe procesa los pagos y realiza
          las transferencias conforme a sus condiciones, plazos, verificaciones y tarifas.
        </li>
        <li>
          Los pagos integrados son opcionales. Su tarifa total incluye el procesamiento de Stripe y
          la comisión de Hosty, a cargo del establecimiento y sin recargo al comensal. Hosty no
          aplica su parte de comisión a las propinas; el establecimiento asume el coste de Stripe
          sobre el total procesado. Las condiciones de pago deben conocerse antes de activarlo.
        </li>
        <li>
          El establecimiento es responsable de sus ventas, impuestos, devoluciones, reclamaciones
          y disputas con sus clientes.
        </li>
      </ul>

      <h2>5. Uso permitido y prohibiciones</h2>
      <p>Te comprometes a utilizar el servicio de forma lícita y a no:</p>
      <ul>
        <li>Realizar ingeniería inversa, descompilar o intentar extraer el código fuente salvo lo permitido por ley.</li>
        <li>Compartir licencias de forma contraria a lo contratado o eludir limitaciones técnicas.</li>
        <li>Introducir malware, realizar ataques o interferir con la infraestructura.</li>
        <li>Utilizar el servicio para actividades fraudulentas o ilícitas.</li>
        <li>Enviar comunicaciones comerciales no solicitadas a través de nuestros canales de soporte.</li>
      </ul>

      <h2>6. Soporte y WhatsApp Business</h2>
      <p>
        Ofrecemos atención al cliente mediante formulario web, chat integrado y WhatsApp Business.
        Al contactarnos por WhatsApp aceptas que trataremos los datos necesarios para gestionar tu
        consulta conforme a nuestra Política de privacidad.
      </p>
      <p>
        Los tiempos de respuesta pueden variar según la carga del servicio. La tarifa estándar
        aplica en horario laboral; el soporte urgente, fuera de ese horario. Servicios de
        instalación, formación o soporte de pago se regirán por las tarifas indicadas en la web o
        presupuesto acordado.
      </p>

      <h2>7. Disponibilidad y mantenimiento</h2>
      <p>
        Procuramos mantener el servicio disponible de forma continuada, pero no garantizamos
        ausencia total de interrupciones. Podemos realizar tareas de mantenimiento, actualizaciones
        o mejoras que afecten temporalmente al acceso.
      </p>
      <p>
        El TPV local puede seguir operando en el establecimiento aunque exista una incidencia
        temporal de sincronización en la nube, dentro de las capacidades técnicas del producto.
      </p>

      <h2>8. Propiedad intelectual</h2>
      <p>
        {LEGAL_SITE_NAME}, su marca, software, documentación y contenidos del sitio web son propiedad
        del titular del servicio o de sus licenciantes. No adquieres derechos de propiedad sobre el
        software, salvo la licencia de uso concedida.
      </p>
      <p>Conservas la titularidad de los datos comerciales que introduces en tu TPV.</p>

      <h2>9. Limitación de responsabilidad</h2>
      <p>
        En la medida permitida por la ley, {LEGAL_SITE_NAME} no será responsable de daños indirectos,
        lucro cesante o pérdida de datos derivados del uso o imposibilidad de uso del servicio,
        salvo dolo o negligencia grave.
      </p>
      <p>
        Eres responsable de la configuración fiscal y operativa de tu negocio, del cumplimiento de
        la normativa aplicable y de la veracidad de los datos que introduces en el TPV.
      </p>

      <h2>10. Suspensión y terminación</h2>
      <p>Podemos suspender o cancelar el acceso al servicio si:</p>
      <ul>
        <li>Incumples estas condiciones o la legislación aplicable.</li>
        <li>Existe impago de la suscripción u obligaciones contractuales.</li>
        <li>Se detecta un riesgo de seguridad o uso abusivo.</li>
      </ul>
      <p>
        La modalidad autogestionada puede cancelarse en cualquier momento. En la modalidad
        gestionada, la cancelación queda sujeta al compromiso inicial y al presupuesto aceptado.
      </p>

      <h2>11. Protección de datos</h2>
      <p>
        El tratamiento de datos personales se describe en la{' '}
        <Link to={LEGAL_URLS.privacy}>Política de privacidad</Link>. Para solicitar la eliminación
        de datos, consulta{' '}
        <Link to={LEGAL_URLS.dataDeletion}>eliminación de datos</Link>.
      </p>

      <h2>12. Modificaciones</h2>
      <p>
        Podemos modificar estas Condiciones del servicio. La versión vigente estará publicada en{' '}
        {LEGAL_SITE_URL}
        {LEGAL_URLS.terms}. El uso continuado del servicio tras la publicación de cambios
        implica la aceptación de los mismos, salvo que la ley exija un consentimiento expreso.
      </p>

      <h2>13. Ley aplicable y jurisdicción</h2>
      <p>
        Estas condiciones se rigen por la legislación española. Para consumidores, serán competentes
        los tribunales que correspondan según la normativa de protección al consumidor. Para
        relaciones entre empresas, las partes se someten a los juzgados y tribunales del domicilio
        del prestador del servicio, salvo norma imperativa en contrario.
      </p>

      <h2>14. Contacto</h2>
      <p>
        Para cualquier consulta sobre estas condiciones:{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a> o{' '}
        <Link to="/contacto">formulario de contacto</Link>.
      </p>
    </LegalPageShell>
  );
}
